import { useEffect } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { CustomNextImage as Image } from '@crehana/next';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { getImgxUrl } from '@crehana/ui';

import TrackUserScroll from '@/shared/components/TrackUserScroll';

import { CAMPAIGN_AMPLITUDE_PAGE_NAME } from '../../constants';
import type { CampaignProps } from '../../types';
import { ScrollContextProvider } from './components/ScrollContext';
import StickyBar from './components/StickyBar';
import UpdateScroll from './components/UpdateScroll';
import { SECTIONS } from './constants';
import BottomBanner from './sections/BottomBanner';
import Courses from './sections/Courses';
import Hero from './sections/Hero';
import Microdegree from './sections/Microdegree';
import Packs from './sections/Packs';
import Premium from './sections/Premium';

const App: React.FC<React.PropsWithChildren<CampaignProps>> = ({
  seo,
  slug,
  hero,
  banner,
  endDate,
  courses,
  microdegrees,
  isSectionBannerActive,
  isSectionCoursesActive,
  // isSectionHeroActive,
  isSectionMicrodegreesActive,
  isSectionPacksActive,
  isSectionPlansActive,
  packs,
  plans,
}) => {
  const { logEvent } = useAmplitude();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    logEvent({
      event: 'Render Campaign Landing',
      data: {
        ...getSourceEventParams(),
        campaignSlug: slug,
      },
    });
  }, []);

  return (
    <ScrollContextProvider>
      <div className="relative">
        {hero.backgroundDesktop && (
          // TODO: talk with the code owner to fix this code so we can enable this rule again
          // eslint-disable-next-line jsx-a11y/alt-text
          <Image
            quality={80}
            src={hero.backgroundDesktop}
            layout="fill"
            objectFit="cover"
            className="absoute inset-0 z-0"
            placeholder="blur"
            blurDataURL={getImgxUrl(hero.backgroundDesktop, {
              w: 20,
              px: 2,
            })}
          />
        )}
        <>
          <div id={SECTIONS[0]}>
            <UpdateScroll sectionId={SECTIONS[0]} />
            <Hero {...hero} h1={seo?.h1} />
          </div>
          <TrackUserScroll
            amplitudePageName={CAMPAIGN_AMPLITUDE_PAGE_NAME}
            sectionName="Hero End"
          />
        </>

        {isSectionCoursesActive && (
          <>
            <div id={SECTIONS[1]}>
              <UpdateScroll sectionId={SECTIONS[1]} />
              <Courses {...courses} />
            </div>
            <TrackUserScroll
              amplitudePageName={CAMPAIGN_AMPLITUDE_PAGE_NAME}
              sectionName="Courses Banner End"
            />
          </>
        )}
        {isSectionPacksActive && (
          <>
            <div id={SECTIONS[2]}>
              <UpdateScroll sectionId={SECTIONS[2]} />
              <Packs {...packs} />
            </div>
            <TrackUserScroll
              amplitudePageName={CAMPAIGN_AMPLITUDE_PAGE_NAME}
              sectionName="Packs Banner End"
            />
          </>
        )}

        {isSectionPlansActive && (
          <>
            <div id={SECTIONS[3]}>
              <UpdateScroll sectionId={SECTIONS[3]} />
              <Premium {...plans} />
            </div>
            <TrackUserScroll
              amplitudePageName={CAMPAIGN_AMPLITUDE_PAGE_NAME}
              sectionName="Premium End"
            />
          </>
        )}

        {isSectionMicrodegreesActive && (
          <>
            <div id={SECTIONS[4]}>
              <UpdateScroll sectionId={SECTIONS[4]} />
              <Microdegree {...microdegrees} />
            </div>
            <TrackUserScroll
              amplitudePageName={CAMPAIGN_AMPLITUDE_PAGE_NAME}
              sectionName="Microdegree End"
            />
          </>
        )}

        {isSectionBannerActive && (
          <>
            <div id={SECTIONS[5]}>
              <UpdateScroll sectionId={SECTIONS[5]} />

              <BottomBanner
                {...banner}
                h1={seo?.h1}
                endDate={endDate || ''}
                logoDesktop={hero?.logoDesktop}
                logoMobile={hero?.logoMobile}
              />
              <UpdateScroll sectionId={SECTIONS[6]} />
            </div>
            <TrackUserScroll
              amplitudePageName={CAMPAIGN_AMPLITUDE_PAGE_NAME}
              sectionName="Bottom Banner End"
            />
          </>
        )}

        <div id={SECTIONS[6]} />

        <StickyBar
          endDate={endDate}
          titleStickyTimer={banner?.titleStickyTimer}
          seeMoreButtonLabel={hero?.buttonText}
          isSectionCoursesActive={isSectionCoursesActive}
          isSectionPlansActive={isSectionPlansActive}
          isSectionMicrodegreesActive={isSectionMicrodegreesActive}
          isSectionPacksActive={isSectionPacksActive}
        />
      </div>
    </ScrollContextProvider>
  );
};

export default App;
